import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ConnectPayouts, ConnectPayoutsList, ConnectDisputesList } from '@stripe/react-connect-js';
import { ConnectFinanceSection } from './connect-finance-tables';
import COPY from './connect-finance-copy';
import FinButton from './fin-button';
import { FinTableSkeleton } from './fin-skeleton';

// One Stripe component in the shell the rest of this page uses, so a live
// widget and our own tables share one vocabulary for titles, errors and
// retries. Never ConnectPayments or ConnectPaymentDetails: they show Stripe's
// per-charge cost, which is the buy rate these screens exist to keep from a
// restaurant.
//
// `payouts` (ConnectPayouts) is the full component — balance, Pay out,
// schedule, destination account and history in one frame — and it is what
// the Payouts page mounts. `payoutsList` is the standalone history list it
// used to mount instead, kept here because this map is what the panel
// supports rather than a list of today's call sites; nothing mounts it at
// the moment.
const MOUNTS = {
  payouts: ConnectPayouts,
  payoutsList: ConnectPayoutsList,
  disputes: ConnectDisputesList,
};

// Thin by design. The finance session and the Stripe instance built from it
// are minted once, by the container (connect-finance.js) — a single account
// session and a single ConnectComponentsProvider serve every mount site on
// the page, the same way settings/payments/account-sections.js shares one
// session across its own four mounts, rather than one of each per panel.
// Minting per panel used to mean minting again on every tab revisit, since a
// tab that isn't active unmounts its panel; hoisting the session above the
// tabs is what stops that. This component owns only what's genuinely its
// own: the section shell, the loading/error state that follows from the
// `instance`/`failure` it's handed, and the mount itself inside its own
// error boundary — a mount-time throw must not take its sibling panel down
// with it (ConnectPanelBoundary, one level up, is the render-time version of
// the same guard, for a throw the mount's own hooks raise before this ever
// gets to render).
const ConnectEmbeddedPanel = ({
  component,
  title,
  description,
  note,
  instance,
  failure = '',
  // Restaurant-only today, but the admin support block mounts this same
  // panel with every write feature disabled — a later ticket, not this one —
  // so the label is a prop rather than a hardcoded restaurant string, with
  // the restaurant's own copy as the default so today's call sites need no
  // change.
  retryLabel = COPY.restaurant.retry,
  flush = false,
  bare = false,
}) => {
  const Mount = MOUNTS[component];
  // Fix 7 (minor #5): this panel used to render an empty card body for the
  // whole time Stripe's iframe was loading — on a page where every sibling
  // section shows a skeleton instead. `instance` covers the (usually brief)
  // wait for the session hooks above; the mount itself still owns however
  // long Stripe's own script and iframe take beyond that, which nothing on
  // this side of the frame can observe.
  const loading = !failure && !instance;

  // Bare: the body of a card the caller draws (the Disputes page's card,
  // stripe-panel-page.js). No ConnectFinanceSection around it, the failure
  // and the wait in the Finance palette, the retry the same reload the shell
  // offers.
  if (bare) {
    if (failure) {
      return (
        <div className="flex items-center gap-3">
          <p className="text-[13px] text-fin-red">{failure}</p>
          <FinButton onClick={() => window.location.reload()}>{retryLabel}</FinButton>
        </div>
      );
    }
    if (!instance || !Mount) return <FinTableSkeleton rows={4} />;
    return (
      <ErrorBoundary fallback={<p className="text-[13px] text-fin-muted">{COPY.balance.widgetFailed}</p>}>
        <Mount />
      </ErrorBoundary>
    );
  }

  return (
    <ConnectFinanceSection
      title={title}
      description={description}
      error={failure}
      loading={loading}
      retryLabel={retryLabel}
      onRetry={() => window.location.reload()}
      note={note}
      flush={flush}
    >
      {instance && Mount ? (
        <ErrorBoundary
          fallback={
            <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400">
              {COPY.balance.widgetFailed}
            </p>
          }
        >
          <div className={flush ? 'py-4' : 'px-5 py-4'}>
            <Mount />
          </div>
        </ErrorBoundary>
      ) : null}
    </ConnectFinanceSection>
  );
};

export default ConnectEmbeddedPanel;
